import { create } from 'zustand';

type RecentAlertsFeedContext = { type: 'recentAlerts' };
type RecentEventsFeedContext = { type: 'recentEvents' };
type DatabaseEventsFeedContext = { type: 'databaseEvents' };
type SourceDatabaseEventsFeedContext = {
    type: 'sourceDatabaseEvents';
    sourceId: string;
};
type SourceEventsFeedContext = {
    type: 'sourceEvents';
    sourceId: string;
    pageKey?: string;
};
type SourceAlertsFeedContext = { type: 'sourceAlerts'; sourceId: string };
type AlertRuleFeedContext = { type: 'alertRule'; alertRuleId: string };
type AlertsFeedContext = { type: 'alerts' };
type RelatedEventFeedContext = { type: 'relatedEvent'; eventId: string };
type VehicleFeedContext = { type: 'vehicle'; vehicleId: string };
type PersonFeedContext = { type: 'person'; personId: string };
type QueryResultsFeedContext = { type: 'queryResults'; eventId: string };
type ReportEventsFeedContext = { type: 'reportEvents'; reportId: string };

export type FeedContext =
    | RecentAlertsFeedContext
    | RecentEventsFeedContext
    | DatabaseEventsFeedContext
    | SourceDatabaseEventsFeedContext
    | SourceEventsFeedContext
    | SourceAlertsFeedContext
    | AlertRuleFeedContext
    | AlertsFeedContext
    | RelatedEventFeedContext
    | VehicleFeedContext
    | PersonFeedContext
    | QueryResultsFeedContext
    | ReportEventsFeedContext;

export function buildSourceEventsFeedContext(
    sourceId: string,
    pageKey?: string,
): FeedContext {
    return {
        type: 'sourceEvents',
        sourceId,
        pageKey,
    };
}

export function extractReportEventsFeedContext(
    state: unknown,
): ReportEventsFeedContext | undefined {
    if (typeof state !== 'object' || state === null) {
        return undefined;
    }
    const { feedContext } = state as { feedContext?: FeedContext };
    if (
        feedContext?.type === 'reportEvents' &&
        typeof feedContext.reportId === 'string' &&
        feedContext.reportId
    ) {
        return feedContext;
    }
    return undefined;
}

type DataStatus = 'pending' | 'loaded' | 'empty';

type FeedItemSelectHandler = (item: unknown) => void;

interface EventFeedState {
    context: FeedContext | null;
    alternateContexts: FeedContext[];
    requested: boolean;
    retainPanelWhilePending: boolean;
    previousPanelVisible: boolean;
    dataStatus: DataStatus;
    playerCurrentTime: number | null;
    playerIsLive: boolean;
    playerLoopEnabled: boolean | null;
    playerIsPaused: boolean;
    playerHasSeeked: boolean;
    playerIsFullscreen: boolean;
    feedPlayingEventId: string | null;
    feedOnItemSelect: FeedItemSelectHandler | null;
    show: (context: FeedContext, alternateContexts?: FeedContext[]) => void;
    hide: () => void;
    reportDataStatus: (status: DataStatus) => void;
    setPlayerCurrentTime: (currentTime: number | null) => void;
    setPlayerIsLive: (isLive: boolean) => void;
    setPlayerLoopEnabled: (loopEnabled: boolean) => void;
    setPlayerIsPaused: (isPaused: boolean) => void;
    setPlayerHasSeeked: (hasSeeked: boolean) => void;
    setPlayerIsFullscreen: (isFullscreen: boolean) => void;
    resetPlayerState: () => void;
    setFeedInteraction: <T>(
        playingEventId: string | null,
        onItemSelect: ((item: T) => void) | null,
    ) => void;
}

const INITIAL_PLAYER_STATE: {
    playerCurrentTime: number | null;
    playerIsLive: boolean;
    playerLoopEnabled: boolean | null;
    playerIsPaused: boolean;
    playerHasSeeked: boolean;
} = {
    playerCurrentTime: null,
    playerIsLive: true,
    playerLoopEnabled: null,
    playerIsPaused: false,
    playerHasSeeked: false,
};

function isPanelVisible({
    requested,
    dataStatus,
    alternateContexts,
    retainPanelWhilePending,
}: Pick<
    EventFeedState,
    'requested' | 'dataStatus' | 'alternateContexts' | 'retainPanelWhilePending'
>) {
    if (!requested) return false;

    return (
        dataStatus === 'loaded' ||
        (dataStatus === 'empty' && alternateContexts.length > 0) ||
        (dataStatus === 'pending' && retainPanelWhilePending)
    );
}

export function selectFeedPanelVisible(state: EventFeedState) {
    return !state.playerIsFullscreen && isPanelVisible(state);
}

function contextsEqual(a: FeedContext | null, b: FeedContext): boolean {
    if (a === null || a.type !== b.type) return false;

    const aRecord = a as unknown as Record<string, unknown>;
    const bRecord = b as unknown as Record<string, unknown>;
    const bKeys = Object.keys(bRecord);

    return (
        Object.keys(aRecord).length === bKeys.length &&
        bKeys.every((key) => aRecord[key] === bRecord[key])
    );
}

export const useEventFeedStore = create<EventFeedState>((set) => ({
    context: null,
    alternateContexts: [],
    requested: false,
    retainPanelWhilePending: false,
    previousPanelVisible: false,
    dataStatus: 'pending',
    ...INITIAL_PLAYER_STATE,
    playerIsFullscreen: false,
    feedPlayingEventId: null,
    feedOnItemSelect: null,

    show: (context, alternateContexts = []) =>
        set((state) => {
            if (state.requested && contextsEqual(state.context, context)) {
                return state;
            }

            return {
                context,
                alternateContexts,
                requested: true,
                dataStatus: 'pending',
                retainPanelWhilePending: state.requested
                    ? isPanelVisible(state)
                    : state.previousPanelVisible,
            };
        }),

    hide: () =>
        set((state) => ({
            requested: false,
            alternateContexts: [],
            dataStatus: 'pending',
            retainPanelWhilePending: false,
            previousPanelVisible: selectFeedPanelVisible(state),
        })),

    reportDataStatus: (dataStatus) => set({ dataStatus }),

    setPlayerCurrentTime: (playerCurrentTime) => set({ playerCurrentTime }),

    setPlayerIsLive: (playerIsLive) => set({ playerIsLive }),

    setPlayerLoopEnabled: (playerLoopEnabled) => set({ playerLoopEnabled }),

    setPlayerIsPaused: (playerIsPaused) => set({ playerIsPaused }),

    setPlayerHasSeeked: (playerHasSeeked) => set({ playerHasSeeked }),

    setPlayerIsFullscreen: (playerIsFullscreen) => set({ playerIsFullscreen }),

    resetPlayerState: () => set(INITIAL_PLAYER_STATE),

    setFeedInteraction: (feedPlayingEventId, onItemSelect) =>
        set({
            feedPlayingEventId,
            feedOnItemSelect: onItemSelect as FeedItemSelectHandler | null,
            playerHasSeeked: false,
        }),
}));
